import React from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

/**
 * Ch97 · PKT-0792 PR 2 · the four states a list can be in, said out loud.
 *
 * 🔴 WHY THIS EXISTS. A provider pass (H1) found three places in socials/ where
 * "there is nothing to do" and "we could not look" render IDENTICALLY — an empty
 * list either way. For Grant that is not a cosmetic difference: an empty Reels
 * tab means "you are done", and a Reels tab that failed to load ALSO means "you
 * are done", right up until he finds out days later that it did not.
 *
 * The correct shape already existed ONCE in this tree — OverviewTab.tsx:482-494,
 * which distinguishes `posted === null` (unread) from `posted.length === 0`
 * (read, and genuinely nothing). This component is that shape, made reusable, so
 * the other surfaces stop having to remember it.
 *
 * FOUR STATES, and the fourth is the one that is usually missing:
 *   loading    we are fetching. Not empty — ASKING.
 *   error      it failed and we KNOW it failed. Say so, offer the way back.
 *   unread     we could not look at all. NOT zero. `null` data, not `[]`.
 *   empty      we looked, and there is genuinely nothing. This is the good news.
 *
 * It renders NOTHING when there is content — the caller draws its own list.
 */
export interface SocialsListStateProps {
  /** A fetch is in flight and we have nothing to show yet. */
  loading?: boolean;
  /** A failure we are certain of. Null when there is none. */
  error?: string | null;
  /** Offered beside an error. Absent ⇒ no retry is possible, and none is shown. */
  onRetry?: () => void;
  /**
   * NULL means we could not read it — render "unread", never "empty".
   * An array (including an empty one) means we DID read it.
   */
  items: readonly unknown[] | null | undefined;
  /** Shown when we looked and there was genuinely nothing. */
  emptyMessage: string;
  /** Shown when we could not look. Says what is unknown, not that it is zero. */
  unreadMessage: string;
  testIdPrefix: string;
}

const SocialsListState: React.FC<SocialsListStateProps> = ({
  loading,
  error,
  onRetry,
  items,
  emptyMessage,
  unreadMessage,
  testIdPrefix,
}) => {
  // ORDER MATTERS. Loading outranks everything: mid-fetch we have no opinion
  // about emptiness yet, and claiming one is the bug this component exists for.
  if (loading) {
    return (
      <Stack direction="row" gap={1.5} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary" data-testid={`${testIdPrefix}-loading`}>
          Loading…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 3 }} data-testid={`${testIdPrefix}-error`}>
        <Typography variant="body2" color="error.main" sx={{ mb: onRetry ? 1 : 0 }}>
          {error}
        </Typography>
        {onRetry ? (
          <Button size="small" variant="outlined" onClick={onRetry} data-testid={`${testIdPrefix}-retry`}>
            Try again
          </Button>
        ) : null}
      </Box>
    );
  }

  // 🔴 NULL IS NOT EMPTY. This is the branch that is usually missing, and the
  // one whose absence is invisible: both render as "nothing here".
  if (items === null || items === undefined) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 3 }}
        data-testid={`${testIdPrefix}-unread`}
      >
        {unreadMessage}
      </Typography>
    );
  }

  if (items.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 3 }}
        data-testid={`${testIdPrefix}-empty`}
      >
        {emptyMessage}
      </Typography>
    );
  }

  return null;
};

export default SocialsListState;
